/**
 * AdminOpsFacultyPhotos — supervised upload UI for faculty portrait
 * gaps. Lists every `key_faculty[]` entry that still has no
 * `photo_url` (grouped by degree) and lets an admin drag-and-drop a
 * JPG/PNG/WEBP. The upload streams to `/api/admin/ops/faculty-photos
 * /upload`, which stores the bytes in Emergent Object Storage under
 * `wua/faculty/{degree_id}/{idx}.{ext}` and points the faculty entry
 * at the public `/api/assets/…` route.
 *
 * Design principles:
 * - No preview crop — ops uploads the photo exactly as they got it
 *   from the professor. Frontend `<img>` renders at 48×48 so any
 *   headshot works.
 * - No batch ZIP upload this session — single-file only. If ops
 *   asks for batch, we ship it as a follow-up (surname → idx
 *   mapping is fiddly enough to warrant its own scoping).
 * - Row disappears from the list on successful upload — so ops has
 *   a shrinking to-do queue.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UploadCloud, Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";


export default function AdminOpsFacultyPhotos() {
  const [pending, setPending] = useState(null);   // {degrees, total_missing_photos}
  const [busy, setBusy] = useState({});           // `${degree_id}::${idx}` → true
  const [includeHotlinked, setIncludeHotlinked] = useState(false);

  async function loadPending() {
    try {
      const r = await api.get(
        `/admin/ops/faculty-photos/pending?include_hotlinked=${includeHotlinked}`
      );
      setPending(r.data);
    } catch (e) {
      toast.error("Couldn't load pending list — check admin auth.");
    }
  }

  useEffect(() => { loadPending(); }, [includeHotlinked]);

  async function handleUpload(degree_id, faculty_idx, file) {
    if (!file) return;
    const key = `${degree_id}::${faculty_idx}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("degree_id", degree_id);
      fd.append("faculty_idx", String(faculty_idx));
      fd.append("file", file);
      const r = await api.post("/admin/ops/faculty-photos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name} → ${r.data.photo_url}`);
      // Optimistically drop the row from the in-memory list rather
      // than refetch — the admin sees their gap queue shrink live.
      setPending((prev) => {
        if (!prev) return prev;
        const degrees = prev.degrees
          .map((d) => {
            if (d.degree_id !== degree_id) return d;
            const gaps = d.gaps.filter((g) => g.faculty_idx !== faculty_idx);
            return { ...d, gaps };
          })
          .filter((d) => d.gaps.length > 0);
        return {
          ...prev,
          degrees,
          total_missing_photos: prev.total_missing_photos - 1,
          total_degrees_with_gaps: degrees.length,
        };
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(String(detail));
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  }

  if (!pending) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-stone-50"
      data-testid="admin-ops-faculty-photos"
    >
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="font-mono text-[11px] uppercase tracking-widest text-stone-500 mb-1">
            Admin · Ops
          </div>
          <h1 className="font-editorial text-4xl text-stone-900">
            Faculty photo backfill
          </h1>
          <p className="text-stone-600 mt-2 max-w-2xl">
            Wikipedia + department-URL scraping covers ~50% of flagship
            faculty portraits. The residual gap is either Cloudflare-blocked
            (Cardiff), URL-rotted (Oxford, KCL, Edinburgh) or client-rendered
            SPAs (UCL, Imperial). Drop a JPG/PNG/WEBP below — each row has
            one-click Google/Bing/Wikipedia links to help you find one fast.
            Files stream to Emergent Object Storage and land on the
            DegreeDetail page immediately.
          </p>
          <div
            className="mt-4 inline-flex items-center gap-4 font-mono text-[12px] text-stone-700 bg-white border border-stone-200 rounded-md px-4 py-2"
            data-testid="admin-ops-faculty-photos-totals"
          >
            <span>
              <strong className="text-stone-900">{pending.total_missing_photos}</strong> photos missing
            </span>
            {typeof pending.total_hotlinked_photos === "number" && pending.total_hotlinked_photos > 0 && (
              <>
                <span className="text-stone-300">·</span>
                <span title="Faculty with an external hotlinked photo URL (e.g. cardiff.ac.uk, oxford.ac.uk) that may render broken because the host blocks cross-origin loads.">
                  <strong className="text-stone-900">{pending.total_hotlinked_photos}</strong> hotlinked (may be broken)
                </span>
              </>
            )}
            <span className="text-stone-300">·</span>
            <span>
              across <strong className="text-stone-900">{pending.total_degrees_with_gaps}</strong> degree{pending.total_degrees_with_gaps === 1 ? "" : "s"}
            </span>
          </div>
          <div className="mt-3">
            <label className="inline-flex items-center gap-2 text-[12px] text-stone-700 font-mono cursor-pointer">
              <input
                type="checkbox"
                checked={includeHotlinked}
                onChange={(e) => setIncludeHotlinked(e.target.checked)}
                data-testid="admin-ops-toggle-hotlinked"
                className="h-3.5 w-3.5"
              />
              Also show faculty with external hotlinked URLs (Cardiff, others behind Cloudflare)
            </label>
          </div>
        </div>

        {pending.degrees.length === 0 && (
          <div className="bg-white border border-stone-200 rounded-md px-6 py-12 text-center">
            <p className="font-serif text-xl text-stone-800">
              All flagship faculty carry a portrait.
            </p>
            <p className="text-stone-600 mt-1 text-sm">
              When the next batch seeds, come back here to backfill.
            </p>
          </div>
        )}

        <div className="space-y-6">
          {pending.degrees.map((d) => (
            <DegreeCard
              key={d.degree_id}
              degree={d}
              busy={busy}
              onUpload={handleUpload}
            />
          ))}
        </div>
      </div>
    </div>
  );
}


function DegreeCard({ degree, busy, onUpload }) {
  return (
    <section
      className="bg-white border border-stone-200 rounded-md overflow-hidden"
      data-testid={`admin-ops-faculty-degree-${degree.degree_id}`}
    >
      <header className="px-5 py-3 bg-stone-100 border-b border-stone-200 flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-stone-500">
            {degree.university_id} · {degree.subject_id || "—"}
          </div>
          <h2 className="font-editorial text-lg text-stone-900 mt-0.5">
            {degree.degree_title}
          </h2>
        </div>
        <Link
          to={`/degree/${degree.degree_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] font-mono text-indigo-700 hover:underline inline-flex items-center gap-1"
          data-testid={`admin-ops-open-degree-${degree.degree_id}`}
        >
          View live <ExternalLink className="h-3 w-3" />
        </Link>
      </header>
      <ul>
        {degree.gaps.map((g) => (
          <FacultyRow
            key={`${degree.degree_id}::${g.faculty_idx}`}
            degree_id={degree.degree_id}
            gap={g}
            busy={busy[`${degree.degree_id}::${g.faculty_idx}`]}
            onUpload={onUpload}
          />
        ))}
      </ul>
    </section>
  );
}


function FacultyRow({ degree_id, gap, busy, onUpload }) {
  // Quick-source helpers — clicking any of these opens a
  // pre-filled search in a new tab so ops can grab a portrait in
  // one hop. Cheaper + higher-yield than another automated scrape.
  const q = encodeURIComponent(
    `${(gap.name || "").replace(/^(Prof\.|Dr\.|Sir)\s+/i, "")} ${gap.role || ""}`.trim()
  );
  const qImage = encodeURIComponent(
    `${(gap.name || "").replace(/^(Prof\.|Dr\.|Sir)\s+/i, "")} portrait`
  );
  const uniHost = (() => {
    try {
      return gap.department_url ? new URL(gap.department_url).hostname : null;
    } catch { return null; }
  })();
  const googleImgUrl = `https://www.google.com/search?tbm=isch&q=${qImage}${uniHost ? `+site%3A${uniHost}` : ""}`;
  const wikiUrl = `https://en.wikipedia.org/wiki/Special:Search?search=${q}`;
  const bingImgUrl = `https://www.bing.com/images/search?q=${qImage}${uniHost ? `+site%3A${uniHost}` : ""}`;

  return (
    <li
      className="px-5 py-3 border-b border-stone-100 last:border-0 flex items-center gap-4"
      data-testid={`admin-ops-row-${degree_id}-${gap.faculty_idx}`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-medium text-stone-900 truncate">
          {gap.name}
        </div>
        <div className="text-[12px] text-stone-500 truncate">
          {gap.role || "—"}
          {gap.photo_source_status && (
            <span className="ml-2 font-mono text-[10px] text-stone-400">
              [{gap.photo_source_status}]
            </span>
          )}
          {gap.hotlinked_photo_url && (
            <span className="ml-2 font-mono text-[10px] text-amber-700">
              hotlinked → may render broken
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {gap.department_url && (
            <a
              href={gap.department_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-mono text-indigo-700 hover:underline inline-flex items-center gap-1"
              data-testid={`admin-ops-dept-${degree_id}-${gap.faculty_idx}`}
            >
              dept profile <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <a
            href={googleImgUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] font-mono text-stone-600 hover:text-indigo-700 hover:underline"
            data-testid={`admin-ops-google-${degree_id}-${gap.faculty_idx}`}
            title="Google Images search, scoped to the department domain when possible"
          >
            google images
          </a>
          <a
            href={bingImgUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] font-mono text-stone-600 hover:text-indigo-700 hover:underline"
          >
            bing images
          </a>
          <a
            href={wikiUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] font-mono text-stone-600 hover:text-indigo-700 hover:underline"
          >
            wikipedia
          </a>
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <Input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={busy}
          onChange={(e) => onUpload(degree_id, gap.faculty_idx, e.target.files?.[0])}
          className="w-56 h-9 text-[11px]"
          data-testid={`admin-ops-file-${degree_id}-${gap.faculty_idx}`}
        />
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin text-stone-500" />
        ) : (
          <UploadCloud className="h-4 w-4 text-stone-500" />
        )}
      </label>
    </li>
  );
}
